import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';

// Actions
import { getHouseholdById } from '@/redux/modules/household';

// Components
import NavigationSidebar from '@/views/components/navigation/NavigationSidebar';
import { ContactDetails } from '../../contacts';
import { Header } from '@/views/components/layout';

// Types
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { GenericHouseHold, Household } from '@/types/households';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { getContactName } from '@/utils/contacts';
import { getContactById, loadContactData, setPersistenceSource } from '@/redux/modules/contacts';

type StateProps = {
  currentHousehold?: GenericHouseHold;
  currentContact?: CurrentContact;
};

type DispatchProps = {
  getContactById: (id: string) => Promise<GenericContact>;
  getHouseholdById: (id: string) => Promise<Household>;
  loadContactData: (contact: GenericContact) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = DispatchProps &
  StateProps & {
    user: User;
  };

const HouseholdContactDetails = (props: Props) => {
  const { currentContact, currentHousehold, user, getContactById, getHouseholdById, loadContactData } = props;
  const { householdId } = useParams<{ householdId: string }>();

  useEffect(() => {
    getHouseholdInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHouseholdInfo = async () => {
    if (householdId) {
      await getHouseholdById(householdId);
    }
  };

  const getNavSidebarItems = () => {
    if (!currentHousehold) {
      return [];
    }

    const housholdNavItem = {
      id: currentHousehold.id,
      value: `/households/${currentHousehold.id}`,
      label: currentHousehold.name,
      icon: 'ds-groups',
    };

    const contactNavItems =
      currentHousehold.contacts.map(contact => ({
        id: contact._id!,
        value: `/households/${currentHousehold.id}/contacts/${contact._id}`,
        label: getContactName(contact) || '',
      })) || [];

    return [housholdNavItem, ...contactNavItems];
  };

  const onClickContact = async (id: string) => {
    if (id === householdId) {
      return;
    }

    if (id != null) {
      const contact = await getContactById(id);
      loadContactData(contact);
    }
  };

  return (
    <div className="household-landing household-contact-details">
      <Header />

      <div className="household-landing-content">
        <NavigationSidebar items={getNavSidebarItems()} defaultActiveId={currentContact?.contact._id} defaultExpanded onClick={onClickContact} />

        <main id="content" className="household-details-container">
          <div className="household-details-body">
            <div className="household-details-content">
              <ContactDetails user={user} isHouseholdContext />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentHousehold: state.households.currentHousehold,
  currentContact: state.contacts.current,
});

export default connect(mapStateToProps, {
  getContactById,
  getHouseholdById,
  loadContactData,
  setPersistenceSource,
})(HouseholdContactDetails);
